import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Initial state
const initialState = {
    // Stato del form
    formData: {
        jobType: null,
        customer: null,
        system: null,
        systemType: null,
        title: "",
        description: "",
        technicians: 1,
        estimatedHours: 2,
        startDate: null,
        endDate: null,
        materials: [],
        attachments: [],
        assistanceRequest: null
    },
    
    // Stato di avanzamento del wizard
    currentStep: 1,
    totalSteps: 3,
    stepsCompleted: [],
    
    // Stato di caricamento e errori
    loading: false,
    error: null,
    success: false,
    
    // Opzioni per i selettori
    customerSystems: [],
    customerSystemsLoading: false,
    customerSystemsError: null,
    
    // Richieste di assistenza
    assistanceRequests: [],
    assistanceRequestsLoading: false,
    assistanceRequestsError: null
};

// Thunk per ottenere le richieste di assistenza
export const getAssistanceRequests = createAsyncThunk(
    "jobWizard/getAssistanceRequests",
    async ({ accountId, token, search = '' }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/assistanceRequests/list`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    accountId,
                    search,
                    length: 100 // Limitiamo a 100 risultati per praticità
                }
            });
            return response.data.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero delle richieste di assistenza");
        }
    }
);

// Thunk per ottenere i sistemi di un cliente
export const getCustomerSystems = createAsyncThunk(
    "jobWizard/getCustomerSystems",
    async ({ customerId, token, accountId }, { rejectWithValue }) => {
        try {
            console.log('Chiamando API per sistemi cliente:', { customerId, accountId });
            
            if (!accountId) {
                // Se non abbiamo accountId, lo otteniamo dal localStorage
                const user = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
                accountId = user.accountId;
            }
            
            // Usiamo l'API corretta systemsList con entrambi i parametri necessari
            const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/systems/systemsList`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    customerId,
                    accountId: accountId // Aggiungiamo accountId richiesto dall'API
                }
            });
            
            console.log('Risposta API sistemi cliente:', response.data);
            return response.data || []; // L'API restituisce direttamente l'array di sistemi
        } catch (error) {
            console.error('Errore nel recupero dei sistemi del cliente:', error);
            return rejectWithValue(error.response?.data || "Errore nel recupero dei sistemi del cliente");
        }
    }
);

// Thunk per la creazione di una nuova commessa
export const createJob = createAsyncThunk(
    "jobWizard/createJob",
    async ({ job, token }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL || ''}/jobs/`, {
                job
            }, {
                headers: {
                    "auth-token": token
                }
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nella creazione della commessa");
        }
    }
);

// Job Wizard Slice
const jobWizardSlice = createSlice({
    name: "jobWizard",
    initialState,
    reducers: {
        // Aggiornamento dei dati del form
        updateFormData: (state, action) => {
            state.formData = {
                ...state.formData,
                ...action.payload
            };
        },
        
        // Aggiornamento di un singolo campo
        updateFormField: (state, action) => {
            const { field, value } = action.payload;
            state.formData[field] = value;
        },
        
        // Gestione step
        nextStep: (state) => {
            if (state.currentStep < state.totalSteps) {
                state.stepsCompleted.push(state.currentStep);
                state.currentStep += 1;
            }
        },
        prevStep: (state) => {
            if (state.currentStep > 1) {
                state.currentStep -= 1;
                // Rimuovi lo step corrente dai completati se si torna indietro
                state.stepsCompleted = state.stepsCompleted.filter(step => step !== state.currentStep);
            }
        },
        
        // Reset del form
        resetForm: (state) => {
            return initialState;
        },
        
        // Aggiunta di materiali
        addMaterial: (state, action) => {
            state.formData.materials.push(action.payload);
        },
        removeMaterial: (state, action) => {
            const index = action.payload;
            state.formData.materials = state.formData.materials.filter((_, i) => i !== index);
        },
        
        // Aggiunta di allegati
        addAttachment: (state, action) => {
            state.formData.attachments.push(action.payload);
        },
        removeAttachment: (state, action) => {
            const index = action.payload;
            state.formData.attachments = state.formData.attachments.filter((_, i) => i !== index);
        }
    },
    extraReducers: (builder) => {
        builder
            // Gestione getAssistanceRequests
            .addCase(getAssistanceRequests.pending, (state) => {
                state.assistanceRequestsLoading = true;
                state.assistanceRequestsError = null;
            })
            .addCase(getAssistanceRequests.fulfilled, (state, action) => {
                state.assistanceRequestsLoading = false;
                state.assistanceRequests = action.payload;
            })
            .addCase(getAssistanceRequests.rejected, (state, action) => {
                state.assistanceRequestsLoading = false;
                state.assistanceRequestsError = action.payload;
            })
            
            // Gestione getCustomerSystems
            .addCase(getCustomerSystems.pending, (state) => {
                state.customerSystemsLoading = true;
                state.customerSystemsError = null;
            })
            .addCase(getCustomerSystems.fulfilled, (state, action) => {
                state.customerSystemsLoading = false;
                state.customerSystems = action.payload;
            })
            .addCase(getCustomerSystems.rejected, (state, action) => {
                state.customerSystemsLoading = false;
                state.customerSystemsError = action.payload;
            })
            
            // Gestione createJob
            .addCase(createJob.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createJob.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(createJob.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

// Export actions
export const { 
    updateFormData, 
    updateFormField, 
    nextStep, 
    prevStep, 
    resetForm,
    addMaterial,
    removeMaterial,
    addAttachment,
    removeAttachment
} = jobWizardSlice.actions;

// Export selectors
export const selectFormData = (state) => state.jobWizard.formData;
export const selectCurrentStep = (state) => state.jobWizard.currentStep;
export const selectTotalSteps = (state) => state.jobWizard.totalSteps;
export const selectStepsCompleted = (state) => state.jobWizard.stepsCompleted;
export const selectLoading = (state) => state.jobWizard.loading;
export const selectError = (state) => state.jobWizard.error;
export const selectSuccess = (state) => state.jobWizard.success;
export const selectCustomerSystems = (state) => state.jobWizard.customerSystems;
export const selectCustomerSystemsLoading = (state) => state.jobWizard.customerSystemsLoading;
export const selectCustomerSystemsError = (state) => state.jobWizard.customerSystemsError;
export const selectAssistanceRequests = (state) => state.jobWizard.assistanceRequests;
export const selectAssistanceRequestsLoading = (state) => state.jobWizard.assistanceRequestsLoading;
export const selectAssistanceRequestsError = (state) => state.jobWizard.assistanceRequestsError;

// Export reducer
export default jobWizardSlice.reducer;
